import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import axios from 'axios';
import translate from '../../../commonComponents/translate';
import withCSRF from '../../../commonComponents/withCSRF';
import QuestionProp from '../propTypes/questionProp';
import { compose, QUESTION_STATUS } from '../../../services/utilityFunctions';
import Request from '../../../services/request';
import Notifications from '../../../services/notifications';
import QuizResultModal from '../modal/QuizResultModal';
import { useModal } from '../../../services/customHooks';
import GenericModal from '../../../commonComponents/modal/GenericModal';

function QuizDisplay({ t, _csrf, questions, onQuizDone, gameId, courseId, lessonID }) {
  const [quizModal, setQuizModal] = useModal({ show: false });
  const [imageModal, setImageModal] = useModal({ show: false, data: null });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [localQuestions, setLocalQuestions] = useState(questions);
  const {
    _id: questionId,
    question,
    answers,
    status,
    userAnswer,
    lesson: lessonId,
    image = {},
  } = localQuestions[selectedIndex] || {};
  const { medium = null } = image || {};
  const [selectedAnswer, setSelectedAnswer] = useState(userAnswer);
  const [alreadyAnswered, setAlreadyAnswered] = useState(status !== QUESTION_STATUS.NOT_ANSWERED);
  const [buttonDisabled, setButtonDisabled] = useState(alreadyAnswered || !selectedAnswer);

  /** API REQUEST - GET THE QUIZ RESULTS */
  // Getting the questions inside the Quiz Display component
    // fixes the problem with going back and going forward inside the lesson
  const getLesson = () => {
      const params = {
        game: gameId,
        populate: 'modules',
      };
      axios.get(
        `/api/academy/course/${courseId}`,
        {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
          },
          params,
        })
        .then(({ data: response }) => {
          let questionList;
          for (const mod of response.data.modulesList) {
            const tempLesson = mod.lessonsList.find(l => l._id === lessonID);
            if (tempLesson) {
              questionList = tempLesson;
              break;
            }
          }
          setLocalQuestions(questionList.questionsList);
          setSelectedIndex(0);
        })
        .catch(err => {
          console.log(err);
        });
    };
  // request when the original questions change
  useEffect(() => {
    getLesson();
  }, [questions]);
  // GET the quiz results so it can display the module
  const getQuizResults = () => {
    axios.get(
      `/api/lesson/${lessonId}/quiz/results`,
      {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
        },
      })
      .then(({ data: response }) => {
        setQuizModal({ show: true, data: { results: response.data } });
      })
      .catch(err => {
        console.log(err);
      });
  };

  useEffect(() => setSelectedAnswer(userAnswer), [userAnswer]);
  useEffect(() => setAlreadyAnswered(status !== QUESTION_STATUS.NOT_ANSWERED), [status]);
  useEffect(() => setButtonDisabled(alreadyAnswered || !selectedAnswer), [alreadyAnswered]);
  useEffect(
    () => {
      const nextUnanswered = localQuestions.findIndex(
        q => q.status === QUESTION_STATUS.NOT_ANSWERED,
      );
      if (nextUnanswered !== -1) {
        setSelectedAnswer(null);
        setSelectedIndex(nextUnanswered);
      } else {
        getQuizResults();
      }
    },
    [],
  );

  const handleSelectedAnswer = answerId => {
    setSelectedAnswer(answerId);
    setButtonDisabled(false);
  };

  const resetQuiz = () => {
    axios.delete(
      `/api/lesson/${lessonId}/user/results`,
      {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(() => {
        window.location.reload();
      })
      .catch(err => {
        console.log(err);
      });
  };

  const handleSubmit = () => {
    if (!selectedAnswer) {
      Notifications.showNotificationWarning(t('academyDetails_lessonType_quiz'), t('ERROR_LESSON_QUIZ_ANSWER_REQUIRED'));
      return;
    }
    let url = '/academy/answer';
    if (gameId) {
      url += `?gameId=${gameId}`;
    }
    new Request(_csrf)
      .put(url, { quizId: questionId, answerId: selectedAnswer })
      .done(({ data }) => {
        const updatedQuestions = localQuestions.map(q => {
          if (q._id === questionId) {
            return { ...q, status: data.status };
          }
          return q;
        });
        setLocalQuestions(updatedQuestions);
        const nextUnanswered = updatedQuestions.findIndex(
          q => q.status === QUESTION_STATUS.NOT_ANSWERED,
        );
        if (nextUnanswered !== -1) {
          setSelectedAnswer(null);
          setSelectedIndex(nextUnanswered);
        } else {
          getQuizResults();
        }
        setAlreadyAnswered(true);
        setButtonDisabled(true);
      })
      .fail(err => {
        Notifications.showErrorFromRequest(err, 'error_answerQuestion');
      });
  };

  const indicatorClass = index =>
    cx('quiz-question-indicator', {
      'status-correct':
        localQuestions[index].status === QUESTION_STATUS.CORRECT,
      'status-incorrect':
        localQuestions[index].status === QUESTION_STATUS.INCORRECT,
    });
  const answerButtonClass = answerId =>
    cx('quiz-answer-list-item', {
      selected: answerId === selectedAnswer,
      'status-correct': status === QUESTION_STATUS.CORRECT,
      'status-incorrect': status === QUESTION_STATUS.INCORRECT,
    });
  const submitButtonClass = cx(
    'btn m-btn--pill btn-dynamic m-btn--wide loader-button', {
      disabled: buttonDisabled,
    });

  // If approved complete lesson, else reset quiz
  const handleQuizModal = async approved => {
    if (approved === false) {
      resetQuiz();
    }
    if (approved) {
      await onQuizDone();
    }
  };

  const handleImagePopup = close => {
    if (close) {
      setImageModal({ show: false, data: null });
    } else {
      setImageModal({ show: true, data: { image: medium } });
    }
  };

  return (
    <>
      {imageModal.show && imageModal.data.image && (
        <GenericModal
          modalId="quizImageModal"
          title={question}
          t={t}
          message={
            <img src={imageModal.data.image} alt={imageModal.data.image} />
          }
          containerClass="quiz-image-modal-container"
          saveMessage={t('close')}
          onClose={() => handleImagePopup(true)}
        />
      )}
      {quizModal.show && quizModal.data.results && (
        <QuizResultModal
          results={quizModal.data.results}
          t={t}
          onClose={async approved => {
            await handleQuizModal(approved);
            setQuizModal({ show: false, data: null });
          }}
        />
      )}
      <div className="course-quiz-wrapper">
        <div className="m-portlet m-portlet--rounded">
          <div className="m-portlet__body">
            <div className="quiz-question-container">
              <div className="quiz-question-question">
                {medium && (
                  <button
                    onClick={() => handleImagePopup(false)}
                    type="button"
                    className="question-image-btn"
                  >
                    <img className="question-image" src={medium} alt={medium} />
                  </button>
                )}
                {question}
              </div>
              <div className="quiz-question-indicator-container">
                {localQuestions.map((q, index) => (
                  <div key={q._id} className={indicatorClass(index)} />
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="m-portlet m-portlet--rounded">
          <div className="m-portlet__body quiz-answers-container">
            <div className="quiz-answer-list">
              {answers && Object.entries(answers).map(
                ([answerId, answer]) => (
                  <button
                    key={answerId}
                    type="button"
                    className={answerButtonClass(answerId)}
                    disabled={alreadyAnswered}
                    onClick={() => handleSelectedAnswer(answerId)}
                  >
                    {answer}
                  </button>
                ),
              )}
            </div>
            <div className="quiz-answer-submit-container">
              <button
                type="button"
                className={submitButtonClass}
                onClick={handleSubmit}
                disabled={buttonDisabled}
              >
                {alreadyAnswered
                  ? t('academyDetails_alreadyAnswered')
                  : t('academyDetails_submitAnswer')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

QuizDisplay.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  questions: PropTypes.arrayOf(PropTypes.shape(QuestionProp.propType))
    .isRequired,
  onQuizDone: PropTypes.func.isRequired,
  gameId: PropTypes.string,
  courseId: PropTypes.string.isRequired,
  lessonID: PropTypes.string.isRequired,
};

QuizDisplay.defaultProps = {
  gameId: null,
};

export default compose(
  translate,
  withCSRF,
)(QuizDisplay);
